import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import { Alert, useMediaQuery } from '@mui/material';
import Picker, { EmojiClickData } from 'emoji-picker-react';
import { useEffect, useRef, useState } from 'react';
import MaterialTextField from 'src/components/material/textField';
import styles from './Challenges.module.scss';

interface ChallengesInputProps {
  onAddChallenge: (challenge: string) => void;
  onEditChallenge: (index: number, updatedChallenge: string) => void;
  onDeleteChallenge: (index: number) => void;
  challenges: string[];
}

const ChallengesInput: React.FC<ChallengesInputProps> = ({
  onAddChallenge,
  onEditChallenge,
  onDeleteChallenge,
  challenges,
}) => {
  const maxActionsPerChallenge = 5;
  const maxLengthActionDescription = 148;

  const isMobile = useMediaQuery('(max-width:545px)');
  const [challengeText, setChallengeText] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [editingEmojiIndex, setEditingEmojiIndex] = useState<number | null>(
    null
  );

  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const editingEmojiPickerRef = useRef<HTMLDivElement>(null);

  const toggleEmojiPicker = () => {
    setIsEmojiOpen((prev) => !prev);
  };

  // TODO solution for positioning the cursor after emoji addition to an input
  // which likely occurs in more than just this place in the application. Split
  // out into reusable form service method for reuse when identified
  const handleEmojiClick = (
    emojiObject: EmojiClickData,
    _event: MouseEvent
  ) => {
    const input = document.querySelector(
      'input[name="action-input"]'
    ) as HTMLInputElement;
    if (!input) {
      return;
    }

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const before = challengeText.slice(0, start);
    const after = challengeText.slice(end);
    const newValue = before + emojiObject.emoji + after;

    setChallengeText(newValue);

    requestAnimationFrame(() => {
      input.focus();
      const newCaret = start + emojiObject.emoji.length;
      // Place the cursor after insertion
      input.setSelectionRange(newCaret, newCaret);

      // Mirror the position of the character in the input to find out
      // where it's positioned so scrollLeft can be used to jump to cursor
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'pre';
      span.style.font = window.getComputedStyle(input).font;
      span.textContent = newValue.slice(0, newCaret);
      document.body.appendChild(span);
      const caretX = span.offsetWidth;
      document.body.removeChild(span);

      input.scrollLeft = caretX - input.clientWidth / 2;
    });

    setIsEmojiOpen(false);
  };

  const handleAddClick = () => {
    if (challengeText.trim() === '') {
      return;
    }
    onAddChallenge(challengeText.trim());
    setChallengeText('');
  };

  const handleSaveEdit = (index: number, updatedText: string) => {
    onEditChallenge(index, updatedText);
    setEditingIndex(null);
    setEditingEmojiIndex(null);
  };

  const toggleEditingEmojiPicker = (index: number) => {
    setEditingEmojiIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleEditingEmojiClick = (
    _event: any,
    emojiObject: any,
    index: number
  ) => {
    const updatedChallenge = challenges[index] + emojiObject.emoji;
    onEditChallenge(index, updatedChallenge);
    setEditingEmojiIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setIsEmojiOpen(false);
      }
      if (
        editingEmojiPickerRef.current &&
        !editingEmojiPickerRef.current.contains(event.target as Node)
      ) {
        setEditingEmojiIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.inputWrapper}>
      <div className={styles.middle}>
        <div className={styles.inputContainer}>
          {/*
          TODO at 148 characters this should be a textarea, anything over 50 or 60
          characters is asking for multiple lines
          */}
          <MaterialTextField
            variant="outlined"
            color="primary"
            placeholder="Action description"
            value={challengeText}
            className={styles.actionInput}
            inputProps={{
              name: 'action-input',
              maxLength: maxLengthActionDescription,
            }}
            onChange={(e) => setChallengeText(e.target.value)}
          />
          <div className={styles.maxDurationText}>
            {challengeText.length} / {maxLengthActionDescription}
          </div>
          <div style={{ position: 'relative', bottom: '72px' }}>
            {!isMobile && (
              <SentimentSatisfiedOutlinedIcon
                onClick={toggleEmojiPicker}
                className={styles.emojiIcon}
              />
            )}
          </div>
          {isEmojiOpen && (
            <div ref={emojiPickerRef} className={styles.emojiPickerContainer}>
              <Picker
                onEmojiClick={handleEmojiClick}
                style={{
                  width: '320px',
                  height: '400px',
                  bottom: '-140px',
                }}
              />
            </div>
          )}
        </div>

        {challenges.length >= maxActionsPerChallenge && (
          <Alert severity="warning" className={styles.actionAlert}>
            Only {maxActionsPerChallenge} Actions are allowed per Challenge
          </Alert>
        )}

        {/*
         TODO disabled buttons doesn't appear disabled to the user they should
         be inert and muted in color
         */}
        {challenges.length < 5 && (
          <button
            className={styles.btn}
            onClick={handleAddClick}
            disabled={challenges.length >= 5}
            style={{ height: '36px' }}
          >
            Add Action
          </button>
        )}

        <div className={styles.challengesList}>
          {challenges.length > 0 && <h2 className={styles.title}>Actions</h2>}

          {challenges.map((challenge, index) => (
            <div key={index} className={styles.challengeItem}>
              {editingIndex === index ? (
                <div className={styles.editContainer}>
                  <MaterialTextField
                    variant="outlined"
                    color="primary"
                    placeholder="Edit challenge"
                    value={challenge}
                    onChange={(e) => onEditChallenge(index, e.target.value)}
                    onBlur={() => handleSaveEdit(index, challenge)}
                  />
                  <SentimentSatisfiedOutlinedIcon
                    onClick={() => toggleEditingEmojiPicker(index)}
                    className={styles.emojiIcon}
                  />
                  {editingEmojiIndex === index && (
                    <div
                      ref={editingEmojiPickerRef}
                      className={styles.emojiPickerContainer}
                    >
                      <Picker
                        onEmojiClick={(e, emoji) =>
                          handleEditingEmojiClick(e, emoji, index)
                        }
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '8px',
                  }}
                >
                  <span style={{ cursor: 'pointer' }}>
                    <IndeterminateCheckBoxIcon
                      sx={{ color: '#888' }}
                      className={styles.deleteIcon}
                      onClick={() => onDeleteChallenge(index)}
                    />
                  </span>
                  <span>{challenge}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChallengesInput;
